import type * as Y from 'yjs';
import type { CommandManager } from '../../../core/commands/CommandManager';
import { CompositeCommand } from '../../../core/commands/ICommand';
import type { SeedProvider } from '../../../core/seeds/SeedRegistry';
import { useOrgChartViewStore } from '../../../stores/orgchartViewStore';
import { CreateEdgeCommand } from '../../../core/commands/CreateEdgeCommand';
import { CreateOrgPersonCommand } from '../commands/CreateOrgPersonCommand';
import { CreateReportsToCommand } from '../commands/CreateReportsToCommand';
import { ORG_PERSON_CARD_SPECS } from '../models/orgChartShapeVariants';
import { ORG_TEMPLATE_SEED_SPECS, type OrgTemplateSeedSpec } from './orgTemplateSeedSpecs';

function writeTemplateSettings(doc: Y.Doc | null | undefined): void {
  if (!doc) return;
  try {
    const map = doc.getMap('diagramSettings');
    const raw = map.get('settings');
    const settings = typeof raw === 'string' ? JSON.parse(raw) : {};
    settings.orgchart = {
      ...(settings.orgchart ?? {}),
      viewMode: 'compact',
      autoLayoutEnabled: false,
    };
    map.set('settings', JSON.stringify(settings));
  } catch (error) {
    console.warn('[orgTemplateSeeds] failed to write settings', error);
  }
}

export async function importOrgTemplateSeed(
  cm: CommandManager,
  template: OrgTemplateSeedSpec,
  doc?: Y.Doc | null,
): Promise<boolean> {
  const people = template.nodes.map(
    (node) =>
      new CreateOrgPersonCommand({
        id: node.id,
        x: node.x,
        y: node.y,
        width: ORG_PERSON_CARD_SPECS.compact.card.width,
        height: ORG_PERSON_CARD_SPECS.compact.card.height,
        name: node.name,
        role: node.role,
        shapeType: 'text',
        accentColor: node.accentColor,
      }),
  );

  const byId = new Map(people.map((command, index) => [template.nodes[index].id, command.getCreatedId()]));
  const reportsTo = template.edges.map((edge, index) => {
    const fromId = byId.get(edge.fromId) ?? edge.fromId;
    const toId = byId.get(edge.toId) ?? edge.toId;
    if (edge.relationshipType) {
      return new CreateEdgeCommand({
        id: `${template.id}-reports-to-${index}`,
        type: edge.relationshipType,
        category: 'org',
        from: fromId,
        to: toId,
        fromAnchor: edge.fromAnchor,
        toAnchor: edge.toAnchor,
        attrs: edge.visible ? { visible: true } : undefined,
      });
    }

    return new CreateReportsToCommand({
      id: `${template.id}-reports-to-${index}`,
      fromId,
      toId,
      dotted: edge.dotted,
      fromAnchor: edge.fromAnchor,
      toAnchor: edge.toAnchor,
    });
  });

  await cm.execute(new CompositeCommand(`Create ${template.title}`, [...people, ...reportsTo]));

  const viewStore = useOrgChartViewStore.getState();
  viewStore.setAutoLayoutEnabled(false);
  viewStore.setLastUsedOrgPersonNodeType('org_person_text');
  viewStore.requestViewportFit();
  writeTemplateSettings(doc);
  cm.clear();
  return true;
}

export const orgTemplateSeeds: SeedProvider[] = ORG_TEMPLATE_SEED_SPECS.map((template) => ({
  id: template.id,
  type: 'org',
  label: template.title,
  provide: () => ({
    format: 'commands',
    build: async (cm, ctx) => {
      await importOrgTemplateSeed(cm, template, ctx?.doc);
    },
  }),
}));
